import {
  DELETE,
  ModeEnum,
  PATCH,
  POST,
  PRICING_RULE,
  UpdateActionEnum,
  request,
  useObservable,
  useObservableValue,
  useStaticSubscription,
  useSubscription,
  useUserContext,
  type Customer,
  type PricingRule,
} from '@talos/kyoko';
import type React from 'react';
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Observable } from 'rxjs';
import { map, scan, shareReplay } from 'rxjs/operators';
import type { CreateCustomerRequest } from './CustomersContext.types';
import { OrgConfigurationKey, useOrgConfiguration } from './OrgConfigurationProvider';

export const PRICING_CONFIG_UPDATE_ID = 'PRICING_CONFIG_UPDATE_ID';

const DEFAULT_PRICING_RULE: PricingRule = {
  Counterparty: '',
  Mode: ModeEnum.Disabled,
};

const PricingRules = createContext<PricingRulesProps | null>(null);
PricingRules.displayName = 'PricingRulesContext';

// Internal-only: lets usePricingRules() trigger the deferred load without exposing it as public API on PricingRulesProps.
const PricingRulesLoadRequest = createContext<(() => void) | null>(null);
PricingRulesLoadRequest.displayName = 'PricingRulesLoadRequestContext';

export const usePricingRules = () => {
  const context = useContext(PricingRules);
  const requestPricingRulesLoad = useContext(PricingRulesLoadRequest);
  if (context == null || requestPricingRulesLoad == null) {
    throw new Error('Missing PricingRules.Provider further up in the tree. Did you forget to add it?');
  }
  // Any consumer needing pricing rules data should trigger the deferred load (a no-op once already loaded/global).
  useEffect(() => {
    requestPricingRulesLoad();
  }, [requestPricingRulesLoad]);
  return context;
};

/** @deprecated Import CreateCustomerRequest from CustomersContext.types. */
export type { CreateCustomerRequest } from './CustomersContext.types';

export interface PricingRulesProps {
  pricingRules: PricingRule[] | undefined;
  pricingRulesByCounterpartyBySymbol: Map<string, Map<string | undefined, PricingRule>>;
  pricingRulesObs: Observable<PricingRule[]>;
  pricingRulesCounterpartyFilter: string | undefined;
  setPricingRulesCounterpartyFilter: (counterparty: string | undefined) => void;
  globalDefault: PricingRule;
  hasGlobalDefault: boolean;
  isGlobalDefaultLoading: boolean;
  isPricingRulesLoading: boolean;
  /** @deprecated Use createCustomer from CustomersProvider. Kept for the legacy customer pricing page. */
  newCounterparty: (data: CreateCustomerRequest) => Promise<unknown>;
  /** @deprecated Use updateCustomer from CustomersProvider. Kept for the legacy customer pricing page. */
  updateCounterparty: (data: Partial<Customer>) => Promise<any>;
  /** @deprecated Use deleteCustomer from CustomersProvider. Kept for the legacy customer pricing page. */
  deleteCounterparty: (counterparty: string) => Promise<any>;
  updatePricingRule: (data: Partial<PricingRule>) => Promise<any>;
}

export const PricingRulesProvider = memo(function PricingRulesProvider(props: React.PropsWithChildren<unknown>) {
  // can't useFeatureFlag() here because it causes a circular dependency.
  const { getConfig } = useOrgConfiguration();
  const pricingRulesPageSize = getConfig(OrgConfigurationKey.PricingRulesPageSize, 5000);
  const requireCustomerPricingRulesCounterpartyFilter = getConfig(
    OrgConfigurationKey.RequireCustomerPricingRulesCounterpartyFilter,
    false
  );
  const loadGlobalCustomerPricingRules = getConfig(OrgConfigurationKey.LoadGlobalCustomerPricingRules, true);
  const [pricingRulesCounterpartyFilter, setPricingRulesCounterpartyFilter] = useState<string | undefined>();
  const [hasRequestedPricingRulesLoad, setHasRequestedPricingRulesLoad] = useState(false);
  const requestPricingRulesLoad = useCallback(() => {
    setHasRequestedPricingRulesLoad(true);
  }, []);

  const shouldSubscribeToPricingRules = loadGlobalCustomerPricingRules || hasRequestedPricingRulesLoad;

  const pricingRulesRequest = useMemo(() => {
    if (!shouldSubscribeToPricingRules) {
      return null;
    }
    if (requireCustomerPricingRulesCounterpartyFilter) {
      if (pricingRulesCounterpartyFilter == null) {
        return null;
      }
      return {
        name: PRICING_RULE,
        Counterparty: pricingRulesCounterpartyFilter,
        tag: 'PricingRulesProvider',
      };
    }
    return { name: PRICING_RULE, limit: pricingRulesPageSize, tag: 'PricingRulesProvider' };
  }, [
    shouldSubscribeToPricingRules,
    requireCustomerPricingRulesCounterpartyFilter,
    pricingRulesCounterpartyFilter,
    pricingRulesPageSize,
  ]);

  const { data: pricingRulesSub, isLoading: isPricingRulesLoading } = useSubscription<PricingRule>(
    pricingRulesRequest,
    { loadAll: true }
  );
  const { data: globalPricingRuleSub, isLoading: isGlobalDefaultLoading } = useStaticSubscription(
    { name: PRICING_RULE, OnlyGlobalRule: true, tag: 'PricingRulesProvider' },
    { loadAll: true }
  );

  const pricingRulesObs = useObservable<PricingRule[]>(
    () =>
      pricingRulesSub.pipe(
        scan((pricingRules, json) => {
          json.initial && pricingRules.clear();
          json.data.forEach((rule: PricingRule) => {
            const key = `${rule.Counterparty}${rule.Symbol}`;
            if (pricingRules.get(key)) {
              pricingRules.delete(key);
            }
            if (rule.Counterparty && rule.UpdateAction !== UpdateActionEnum.Remove && rule.Counterparty) {
              pricingRules.set(key, rule);
            }
          });
          return pricingRules;
        }, new Map()),
        map(pricingRules => [...pricingRules.values()]),
        // refCount: false — this is a provider-lifetime cache. Consumers (e.g. EntityAdmin tabs) can mount/unmount
        // independently (tab switches, route changes) without tearing down and losing the accumulated rules.
        shareReplay({
          bufferSize: 1,
          refCount: false,
        })
      ),
    [pricingRulesSub]
  );

  const GLOBAL_DEFAULT_KEY = 'GLOBAL_DEFAULT';
  const globalPricingRuleObs = useObservable(
    () =>
      globalPricingRuleSub.pipe(
        scan((pricingRules, json) => {
          json.initial && pricingRules.clear();
          json.data.forEach((rule: PricingRule) => {
            // Global default
            const newDefault = rule;
            if (!newDefault.PricingAggregation) {
              // Apply Default Pricing Aggregation "Dealer" to the Global Default
              newDefault.PricingAggregation = 'dealer';
            }
            if (newDefault.Spread) {
              // Delete "Spread" and Pre-Populate "BidSpread" and "OfferSpread"
              if (newDefault.BidSpread == null) {
                newDefault.BidSpread = newDefault.Spread;
              }
              if (newDefault.OfferSpread == null) {
                newDefault.OfferSpread = newDefault.Spread;
              }
              delete newDefault.Spread;
            }
            pricingRules.set(GLOBAL_DEFAULT_KEY, newDefault);
          });
          return pricingRules;
        }, new Map()),
        map(pricingRules => pricingRules.get(GLOBAL_DEFAULT_KEY) ?? DEFAULT_PRICING_RULE),
        // refCount: false — see pricingRulesObs above.
        shareReplay({
          bufferSize: 1,
          refCount: false,
        })
      ),
    [globalPricingRuleSub]
  );

  const hasGlobalDefaultObs = useObservable(
    () =>
      globalPricingRuleSub.pipe(
        scan((hasGlobalDefault, json) => {
          const baseline = json.initial ? false : hasGlobalDefault;
          return baseline || json.data.length > 0;
        }, false),
        // refCount: false — see pricingRulesObs above.
        shareReplay({
          bufferSize: 1,
          refCount: false,
        })
      ),
    [globalPricingRuleSub]
  );

  const globalDefault = useObservableValue(() => globalPricingRuleObs, [globalPricingRuleObs], DEFAULT_PRICING_RULE);
  const hasGlobalDefault = useObservableValue(() => hasGlobalDefaultObs, [hasGlobalDefaultObs], false);
  const pricingRules = useObservableValue(() => pricingRulesObs, [pricingRulesObs]);

  const { orgApiEndpoint } = useUserContext();
  const endpoint = `${orgApiEndpoint}/pricing-rules`;
  const customersEndpoint = `${orgApiEndpoint}/customers`;

  const newCounterparty = useCallback<PricingRulesProps['newCounterparty']>(
    counterparty => request(POST, customersEndpoint, counterparty),
    [customersEndpoint]
  );
  const updatePricingRule = useCallback<PricingRulesProps['updatePricingRule']>(
    pricingRule => request(PATCH, endpoint, pricingRule),
    [endpoint]
  );
  const updateCounterparty = useCallback<PricingRulesProps['updateCounterparty']>(
    counterparty => request(PATCH, customersEndpoint, counterparty),
    [customersEndpoint]
  );
  const deleteCounterparty = useCallback<PricingRulesProps['deleteCounterparty']>(
    counterparty => request(DELETE, `${customersEndpoint}/${encodeURIComponent(counterparty)}`),
    [customersEndpoint]
  );

  const pricingRulesByCounterpartyBySymbol = useObservableValue(
    () =>
      pricingRulesObs.pipe(
        map(pricingRules =>
          pricingRules.reduce((groupedRules, rule) => {
            const { Counterparty, Symbol } = rule;

            let symbolMap = groupedRules.get(Counterparty);
            if (!symbolMap) {
              symbolMap = new Map<string | undefined, PricingRule>();
              groupedRules.set(Counterparty, symbolMap);
            }

            symbolMap.set(Symbol, rule);

            return groupedRules;
          }, new Map<string, Map<string | undefined, PricingRule>>())
        )
      ),
    [pricingRulesObs],
    new Map()
  );

  const value = useMemo<PricingRulesProps>(
    () => ({
      pricingRules,
      pricingRulesByCounterpartyBySymbol,
      pricingRulesObs,
      pricingRulesCounterpartyFilter,
      setPricingRulesCounterpartyFilter,
      globalDefault,
      hasGlobalDefault,
      isGlobalDefaultLoading,
      newCounterparty,
      updatePricingRule,
      deleteCounterparty,
      updateCounterparty,
      isPricingRulesLoading,
    }),
    [
      pricingRules,
      pricingRulesByCounterpartyBySymbol,
      pricingRulesObs,
      pricingRulesCounterpartyFilter,
      setPricingRulesCounterpartyFilter,
      globalDefault,
      hasGlobalDefault,
      isGlobalDefaultLoading,
      newCounterparty,
      updatePricingRule,
      deleteCounterparty,
      updateCounterparty,
      isPricingRulesLoading,
    ]
  );

  return (
    <PricingRulesLoadRequest.Provider value={requestPricingRulesLoad}>
      <PricingRules.Provider value={value}>{props.children}</PricingRules.Provider>
    </PricingRulesLoadRequest.Provider>
  );
});
